/**
 * Pure function to compute flight line definitions from schedule data.
 *
 * Movement ledger model: takes upcoming outbound departures + pending inbound
 * return + current presence, returns a sorted array of FlightLineDef items (max 2).
 */

import type { AircraftSchedule } from '@/types/aircraft-schedules'
import type { PresenceStatus } from '@/types/allocations'
import { formatRelativeTimeShort, formatLocalTime } from './format-business-data'

export interface FlightLineDef {
  id: string
  type: 'departure' | 'arrival'
  /** Primary label: "Departs in 3h" or "Returns in 2d" */
  label: string
  /** Exact local time: "(14:00)" */
  exactTime: string | null
  /** Route indicator: "→ KTEB" or "← KTEB" */
  route: string | null
  /** Full tooltip text for hover */
  tooltipText: string | null
  /** Whether this line should render at 50% opacity (far-future events) */
  isMuted: boolean
  /** What the quick-confirm button does */
  confirmAction: 'depart' | 'arrive' | null
  /** Button label */
  confirmLabel: string | null
  /** The underlying schedule record (for edit dialog) */
  schedule: AircraftSchedule
}

/**
 * Compute flight lines for the compact card.
 *
 * Rules:
 * - If present and there's a scheduled outbound departure → show departure line
 * - If away and there's a pending inbound return → show arrival line
 * - Max 2 lines; second line is muted
 * - Sort: most imminent first
 */
export function computeFlightLines(
  upcomingDepartures: AircraftSchedule[],
  pendingReturn: AircraftSchedule | null,
  presence: PresenceStatus,
): FlightLineDef[] {
  const lines: FlightLineDef[] = []

  // Arrival line: aircraft is away with a pending inbound return
  if (presence === 'away' && pendingReturn) {
    const relTime = formatRelativeTimeShort(pendingReturn.scheduledAt)
    const localTime = formatLocalTime(pendingReturn.scheduledAt)
    lines.push({
      id: pendingReturn.id,
      type: 'arrival',
      label: `Returns ${relTime}`,
      exactTime: `(${localTime})`,
      route: pendingReturn.airportIcao ? `← ${pendingReturn.airportIcao}` : null,
      tooltipText: formatTooltip(pendingReturn.scheduledAt, pendingReturn.airportName),
      isMuted: false,
      confirmAction: 'arrive',
      confirmLabel: 'Confirm',
      schedule: pendingReturn,
    })
  }

  // Paired return line: aircraft is present with an upcoming departure that has a paired return
  if (presence === 'present' && pendingReturn && upcomingDepartures.length > 0) {
    const hasPairedDeparture = upcomingDepartures.some(dep => pendingReturn.pairId === dep.id)
    if (hasPairedDeparture) {
      const relTime = formatRelativeTimeShort(pendingReturn.scheduledAt)
      const localTime = formatLocalTime(pendingReturn.scheduledAt)
      lines.push({
        id: pendingReturn.id,
        type: 'arrival',
        label: `Returns ${relTime}`,
        exactTime: `(${localTime})`,
        route: pendingReturn.airportIcao ? `← ${pendingReturn.airportIcao}` : null,
        tooltipText: formatTooltip(pendingReturn.scheduledAt, pendingReturn.airportName),
        isMuted: true,
        confirmAction: null,
        confirmLabel: null,
        schedule: pendingReturn,
      })
    }
  }

  // Departure lines from upcoming outbound schedules
  for (const sched of upcomingDepartures) {
    const relTime = formatRelativeTimeShort(sched.scheduledAt)
    const localTime = formatLocalTime(sched.scheduledAt)
    const hoursUntil = (new Date(sched.scheduledAt).getTime() - Date.now()) / (1000 * 60 * 60)

    lines.push({
      id: sched.id,
      type: 'departure',
      label: `Departs ${relTime}`,
      exactTime: `(${localTime})`,
      route: sched.airportIcao ? `→ ${sched.airportIcao}` : null,
      tooltipText: formatTooltip(sched.scheduledAt, sched.airportName),
      isMuted: hoursUntil > 24,
      confirmAction: presence === 'present' ? 'depart' : null,
      confirmLabel: presence === 'present' ? 'Confirm' : null,
      schedule: sched,
    })
  }

  // Sort: imminent first
  lines.sort((a, b) => {
    const timeA = a.schedule.scheduledAt
    const timeB = b.schedule.scheduledAt
    return new Date(timeA).getTime() - new Date(timeB).getTime()
  })

  // Max 2 lines, mark second as muted
  const result = lines.slice(0, 2)
  if (result.length > 1) {
    result[1] = { ...result[1], isMuted: true }
  }

  return result
}

function formatTooltip(dateStr: string, name: string | null): string {
  const d = new Date(dateStr)
  const dayStr = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  const parts = [`${dayStr} · ${timeStr} local`]
  if (name) parts.push(name)
  return parts.join('\n')
}
